/*
 * Loading history into the model. The reducer owns what arrives over the socket; this file owns
 * what is fetched, and the two meet on the same three fields — `conversations[c].status`,
 * `messages[c]` and the module's own `self` row.
 *
 * Opening a conversation pins the unread divider before anything is fetched: the divider must sit
 * at the seq the reader *had* when they arrived, and a page that lands a moment later would
 * otherwise move it (spec 12 §4.3).
 *
 * A thread is loaded the same way and kept beside the timeline in the module's own slice: its
 * replies never enter the conversation's window (UX §4.1 R1), so the two windows are separate and
 * only the root — which lives in the timeline — is shared between them.
 */

import { WireError } from "../../../core/net/api.ts"
import type { ConversationId, Model } from "../../../core/store/model.ts"
import { client } from "../../../core/store/store.ts"
import { fetchAfter, fetchAround, fetchBefore, fetchTail, HISTORY_PAGE } from "../api.ts"
import {
	browseChannels,
	fetchMessageBySeq,
	fetchThreadPage,
	listAdminConversations,
	readConversation,
} from "../conversation/api.ts"
import { ReachAdmin } from "../wire/chat.gen.ts"
import type { ConversationReadAnswer, HistoryPage, MessageView } from "../wire/views.ts"
import {
	clearThreads,
	dropOutside,
	putOutside,
	upsertBrowsedConversation,
	upsertConversation,
	upsertSelf,
} from "./reducer.ts"
import { chatSlice, lastReadSeqOf, withChatSlice } from "./selectors.ts"
import type { ThreadWindow } from "./slice.ts"
import {
	capRows,
	evictIdle,
	forgetSweptVisits,
	holdConversation,
	holdThread,
	nextEvictionDue,
	noteLoadedWindows,
	type Bounds,
	type DropEnd,
} from "./trim.ts"

function setStatus(
	model: Model,
	conv: ConversationId,
	status: "loading" | "ready" | "error",
): Model {
	const slice = model.conversations[conv]
	if (!slice) return model
	return { ...model, conversations: { ...model.conversations, [conv]: { ...slice, status } } }
}

/**
 * How a fetched page meets the loaded window: it replaces the window (the tail, the page around a
 * message), goes above it ("older") or below it ("newer"). A merge keeps what the window holds on
 * the far side of the page, so a message that arrived over the socket while the page was on its
 * way is not lost between the two.
 */
type Merge = "replace" | "older" | "newer"

/** Writes one fetched page in. */
function mergePage(model: Model, conv: ConversationId, page: HistoryPage, merge: Merge): Model {
	const existing = model.messages[conv]
	const fetched = page.messages
	const held = (existing?.rows ?? []) as MessageView[]
	let grown: MessageView[]
	let bounds: Bounds
	// Which end of the merged run may be given back is which way the reader is going: somebody
	// paging into the past is leaving the tail behind, somebody paging back down is leaving the top.
	// The flag raised for what went is what the surface then offers — "Load earlier" above, the jump
	// pill below — so the ceiling costs a reader nothing but a fetch they were about to make anyway.
	let drop: DropEnd
	switch (merge) {
		case "older":
			grown = [...fetched, ...held.filter((m) => m.seq > (fetched.at(-1)?.seq ?? 0))]
			bounds = { hasMoreBefore: page.hasMoreBefore, hasMoreAfter: existing?.hasMoreAfter ?? false }
			drop = "newest"
			break
		case "newer":
			grown = [...held.filter((m) => m.seq < (fetched[0]?.seq ?? Infinity)), ...fetched]
			bounds = { hasMoreBefore: existing?.hasMoreBefore ?? false, hasMoreAfter: page.hasMoreAfter }
			drop = "oldest"
			break
		case "replace":
			grown = fetched
			bounds = { hasMoreBefore: page.hasMoreBefore, hasMoreAfter: page.hasMoreAfter }
			drop = "oldest"
			break
	}
	const { rows, hasMoreBefore, hasMoreAfter } = capRows(grown, bounds, drop)
	const first = rows[0]
	const last = rows.at(-1)

	const slice = chatSlice(model)
	let next: Model = {
		...model,
		messages: {
			...model.messages,
			[conv]: {
				rows,
				firstSeq: first?.seq ?? 0,
				lastSeq: last?.seq ?? 0,
				hasMoreBefore,
				hasMoreAfter,
			},
		},
	}

	// A page carries the conversation's own counter as the server had it, and core's gap rule runs
	// on that counter: a refetch that leaves it where the gap was makes the very next message a gap
	// again, this time with a fetch already marked as running — and the conversation stops moving
	// for the life of the tab (protocol.md §2.8).
	//
	// It may be taken over only where nothing arriving at or below the page's head could still have
	// belonged in the loaded window: either the window declares newer messages unloaded, and the
	// reducer appends nothing to it at all (`reducer.ts appendToWindow`), or this page ran to the
	// head itself. The one case where neither holds is a page fetched *above* a window that already
	// reaches the tail — the messages between the window's end and the head are on their way over
	// the socket, and a counter raised past them would drop them as duplicates.
	//
	// The flag that answers it is the merge's own and never the one the ceiling may have just raised.
	// A page of older history that overflows the ceiling raises `hasMoreAfter` by dropping rows the
	// window *held*, at the end the reader is moving away from — which says nothing about the
	// messages between that end and the head, and those are exactly the ones on their way.
	const conversation = model.conversations[conv]
	if (
		conversation &&
		page.lastSeq > conversation.lastSeq &&
		(bounds.hasMoreAfter || !page.hasMoreAfter)
	) {
		next = {
			...next,
			conversations: { ...next.conversations, [conv]: { ...conversation, lastSeq: page.lastSeq } },
		}
	}

	if (page.self) {
		return withChatSlice(next, { ...slice, self: { ...slice.self, [conv]: page.self } })
	}
	// A page carries the reader's own membership row, and the server sends none when they are not
	// an active member of the conversation (`service_history.go`). A missing row is therefore news
	// and not silence: this is the one answer that tells a warm client its membership has ended.
	// Catch-up cannot say it — a sync replays joined scopes only, so the membership event of a
	// conversation the reader has left is never delivered to them — and a client that boots from a
	// stored model never hydrates from a bootstrap. Left as it was, the model kept a membership the
	// server had already ended and the surface drew a composer whose first Enter answers "Not sent:
	// Not found." The row itself stays, because a public channel is readable after a leave; what
	// goes is the membership and the place in the reader's own list.
	if (slice.self[conv] === undefined) return next
	const self = { ...slice.self }
	delete self[conv]
	return withChatSlice(
		{ ...next, conversationOrder: next.conversationOrder.filter((id) => id !== conv) },
		{ ...slice, self },
	)
}

/**
 * Pins the "new messages" divider for this conversation. It is set once per visit: a second call
 * while the reader is still in the conversation keeps the seq they arrived on — which is what makes
 * the line stay where the reader stopped reading rather than follow the read cursor down.
 */
export function pinUnreadAnchor(conv: ConversationId): void {
	client.api.setState((model) => {
		const slice = chatSlice(model)
		if (slice.unreadAnchor[conv] !== undefined) return model
		const anchor = slice.cursors[conv]?.lastReadSeq ?? slice.self[conv]?.lastReadSeq ?? 0
		return withChatSlice(model, {
			...slice,
			unreadAnchor: { ...slice.unreadAnchor, [conv]: anchor },
		})
	})
}

/**
 * Forgets the anchor, which is what ends a visit (X-B3 A3). It lives for as long as the reader is in
 * the conversation and not a second longer: while they are there it keeps the *New messages* line on
 * screen and gives `Ctrl+J` somewhere to go even after the backlog has been read, and once they have
 * left, the next visit draws its line from the cursor they have then instead of from where they once
 * arrived.
 *
 * The read cursor never clears it — that was the defect: opening a conversation moves the cursor to
 * the head within a second, and a line recomputed from it is a line that is never there.
 */
export function clearUnreadAnchor(conv: ConversationId): void {
	client.api.setState((model) => {
		const slice = chatSlice(model)
		if (slice.unreadAnchor[conv] === undefined) return model
		const unreadAnchor = { ...slice.unreadAnchor }
		delete unreadAnchor[conv]
		return withChatSlice(model, { ...slice, unreadAnchor })
	})
}

/** Counts one more replacement of the window: the surface lands again on what arrived. */
function withOpened(model: Model, conv: ConversationId): Model {
	const slice = chatSlice(model)
	const opened = { ...slice.opened, [conv]: (slice.opened[conv] ?? 0) + 1 }
	return withChatSlice(model, { ...slice, opened })
}

/** Records which way the window is growing, or that it is not, for the list's loading row. */
function withPaging(model: Model, conv: ConversationId, paging: "older" | "newer" | null): Model {
	const slice = chatSlice(model)
	const next = { ...slice.paging }
	if (paging === null) delete next[conv]
	else next[conv] = paging
	return withChatSlice(model, { ...slice, paging: next })
}

/**
 * Records the seq of a permalink whose message the page around it did not hold — it is gone — or
 * clears the mark when a page held it. One mark per conversation: the list draws a divider there.
 */
function withGone(model: Model, conv: ConversationId, seq: number): Model {
	const slice = chatSlice(model)
	if ((slice.gone[conv] ?? 0) === seq) return model
	const gone = { ...slice.gone }
	if (seq === 0) delete gone[conv]
	else gone[conv] = seq
	return withChatSlice(model, { ...slice, gone })
}

async function load(
	conv: ConversationId,
	fetchPage: () => Promise<HistoryPage>,
	merge: Merge,
	/** For the page around a permalink: the seq it named, so a page without it marks the gap. */
	expectSeq = 0,
): Promise<void> {
	const store = client.api
	const paging = merge === "replace" ? null : merge
	store.setState((model) => withPaging(setStatus(model, conv, "loading"), conv, paging))
	try {
		const page = await fetchPage()
		// The page lands through the store and not through `setState`, because a page is the end of
		// a fetch and the end of a fetch is the store's bookkeeping: the events that arrived while
		// the page was on its way are replayed against it, the mark that says a fetch is running is
		// taken off, and the count of consecutive refetches is settled (`core/store/store.ts`).
		client.settleConversation(conv, (model) => {
			let merged = setStatus(mergePage(model, conv, page, merge), conv, "ready")
			if (merge === "replace") merged = withOpened(merged, conv)
			const gone = expectSeq !== 0 && !page.messages.some((m) => m.seq === expectSeq)
			return withGone(withPaging(merged, conv, null), conv, gone ? expectSeq : 0)
		})
	} catch {
		// The surface reports it: a failed page leaves a retry row rather than an empty timeline.
		// This ends the fetch as much as a page does — a conversation left marked as fetching would
		// never be refetched again — and the retry count stands, so a conversation whose pages keep
		// failing still reaches the bound.
		client.settleConversation(conv, (model) =>
			withPaging(setStatus(model, conv, "error"), conv, null),
		)
	}
}

/** True while a page of this conversation is on its way; a second request would repeat it. */
function inFlight(model: Model, conv: ConversationId): boolean {
	return model.conversations[conv]?.status === "loading"
}

/** The newest page; what a conversation with nothing unread opens on, and what the pill jumps to. */
export function loadTail(conv: ConversationId): Promise<void> {
	return load(conv, () => fetchTail(conv), "replace")
}

/**
 * One page further into the past, above the loaded window. A call while a page is in flight is
 * refused rather than queued: the sentinel that asks fires once per commit, and the page that is
 * on its way is the answer to every one of those (X-A1 D1).
 */
export function loadOlder(conv: ConversationId): Promise<void> {
	const model = client.api.getState()
	const window = model.messages[conv]
	if (!window || !window.hasMoreBefore || inFlight(model, conv)) return Promise.resolve()
	const before = window.firstSeq
	return load(conv, () => fetchBefore(conv, before), "older")
}

/** One page below the loaded window — the way back down after a permalink jump. */
export function loadNewer(conv: ConversationId): Promise<void> {
	const model = client.api.getState()
	const window = model.messages[conv]
	if (!window || !window.hasMoreAfter || inFlight(model, conv)) return Promise.resolve()
	const after = window.lastSeq
	return load(conv, () => fetchAfter(conv, after), "newer")
}

/**
 * The page around one message — a permalink's first fetch. A seq the page does not hold is marked
 * as gone, so the list can say so at its place rather than showing the tail.
 */
export function loadAround(conv: ConversationId, seq: number): Promise<void> {
	return load(conv, () => fetchAround(conv, seq), "replace", seq)
}

/**
 * Takes the gone mark back: the permalink turned out to name a reply, which is in no timeline page
 * by design (UX §4.1 R1) and is shown in its thread instead.
 */
export function forgetGone(conv: ConversationId): void {
	client.api.setState((model) => withGone(model, conv, 0))
}

/**
 * The same page for a landing on the first unread, which is a *position* and not a message: the
 * seq after the read cursor may belong to a reply and be in no timeline page at all, and that is
 * not a gap worth a divider.
 */
function loadAroundUnread(conv: ConversationId, seq: number): Promise<void> {
	return load(conv, () => fetchAround(conv, seq), "replace")
}

/**
 * The first seq this reader has not read, or 0 when there is nothing to land on. It is the read
 * cursor plus one, never below the floor of what they may see; the count the server keeps says
 * whether there is anything at all, so a cursor that trails the last seq by nothing but replies is
 * not "unread". A reader who has never read anything here — cursor 0 — has no first unread either:
 * the divider is drawn from the anchor and an anchor of 0 draws none (`buildRows`), so they open
 * at the tail the way they always have rather than at the top of a history with no line in it.
 */
export function firstUnreadSeq(model: Model, conv: ConversationId): number {
	const slice = chatSlice(model)
	const self = slice.self[conv]
	if (self === undefined || self.state !== "active") return 0
	if ((model.conversations[conv]?.unread ?? 0) === 0) return 0
	const lastRead = lastReadSeqOf(model, conv)
	if (lastRead === 0) return 0
	return Math.max(lastRead + 1, self.visibleFromSeq)
}

/**
 * `Ctrl+J` when the loaded window does not hold the divider: the page around the first unread,
 * after which the surface's landing scrolls to the divider it draws. Nothing to do when nothing is
 * unread.
 */
export function landOnFirstUnread(conv: ConversationId): Promise<void> {
	const first = firstUnreadSeq(client.api.getState(), conv)
	return first === 0 ? Promise.resolve() : loadAroundUnread(conv, first)
}

/**
 * What opening a conversation loads (X-A1 D3, G-31). A permalink loads the page around its message.
 * Otherwise the reader lands on their first unread: when the unread count says it cannot be inside
 * the tail page, the page around it is fetched instead of the tail; when the count allows it, the
 * tail is fetched and — in the rare case the count was wrong — the page around it is fetched after
 * all. A reader with nothing unread gets the tail.
 */
export async function openConversation(conv: ConversationId, focusSeq?: number): Promise<void> {
	const store = client.api
	const model = store.getState()
	// The surface asks once per cold conversation, but a cold conversation renders twice before
	// its page is in (the anchor is pinned between the two): the second ask is the same ask.
	if (inFlight(model, conv)) return
	if (focusSeq !== undefined) return loadAround(conv, focusSeq)
	const firstUnread = firstUnreadSeq(model, conv)
	if (firstUnread === 0) return loadTail(conv)
	if ((model.conversations[conv]?.unread ?? 0) > HISTORY_PAGE) {
		return loadAroundUnread(conv, firstUnread)
	}
	await loadTail(conv)
	const window = store.getState().messages[conv]
	if (window !== undefined && window.hasMoreBefore && window.firstSeq > firstUnread) {
		await loadAroundUnread(conv, firstUnread)
	}
}

/** Writes one thread window back into the module's slice, keeping every other one identical. */
function withThread(model: Model, rootId: number, window: ThreadWindow): Model {
	const slice = chatSlice(model)
	return withChatSlice(model, { ...slice, threads: { ...slice.threads, [rootId]: window } })
}

const COLD: ThreadWindow = { rows: [], hasMoreBefore: false, status: "cold" }

/**
 * Loads one page of a thread and merges it in. `before` is the seq the page is asked for; without
 * it the page is the newest one and replaces whatever was loaded, which is what makes reopening a
 * pane a refresh rather than a merge of two ages of the same thread.
 */
async function loadThreadPage(
	conv: ConversationId,
	rootId: number,
	before: number | undefined,
): Promise<void> {
	const store = client.api
	store.setState((model) => {
		const current = chatSlice(model).threads[rootId] ?? COLD
		return withThread(model, rootId, { ...current, status: "loading" })
	})
	try {
		const page = await fetchThreadPage(conv, rootId, before)
		store.setState((model) => {
			const current = chatSlice(model).threads[rootId] ?? COLD
			const older = page.messages
			// The page, then whatever the window already held that is newer than it: for an older
			// page that is everything loaded so far, and for the newest page it is the replies that
			// arrived while the request was in flight.
			const rows = [...older, ...current.rows.filter((m) => m.seq > (older.at(-1)?.seq ?? 0))]
			const window: ThreadWindow = {
				rows,
				hasMoreBefore: page.hasMoreBefore,
				status: "ready",
			}
			// The root travels on every page; an older page that somehow carries none keeps the one
			// the pane opened with rather than losing its head.
			const root = page.root ?? current.root
			if (root !== undefined) window.root = root
			return withThread(model, rootId, window)
		})
	} catch {
		// The pane reports it and offers another try; an empty pane with no explanation would look
		// like a thread whose replies had been removed.
		store.setState((model) => {
			const current = chatSlice(model).threads[rootId] ?? COLD
			return withThread(model, rootId, { ...current, status: "error" })
		})
	}
}

/** The newest page of a thread — what opening a pane shows. */
export function loadThread(conv: ConversationId, rootId: number): Promise<void> {
	return loadThreadPage(conv, rootId, undefined)
}

/** One page further into the past, above the loaded replies. */
export function loadOlderReplies(conv: ConversationId, rootId: number): Promise<void> {
	const window = chatSlice(client.api.getState()).threads[rootId]
	const oldest = window?.rows[0]?.seq
	if (!window || !window.hasMoreBefore || window.status === "loading" || oldest === undefined) {
		return Promise.resolve()
	}
	return loadThreadPage(conv, rootId, oldest)
}

/**
 * What a permalink addresses: the id of the message at that seq and, when it is a reply, the thread
 * it lives in — the conversation's own window will never show it (UX §4.1 R1), so the route needs
 * to know whether to open a pane. A message that cannot be read answers nothing, and the route
 * falls back to the conversation itself rather than to an error page.
 */
export async function resolvePermalink(
	conv: ConversationId,
	seq: number,
): Promise<{ id: number; seq: number; threadRootId?: number } | null> {
	try {
		const { message } = await fetchMessageBySeq(conv, seq)
		const found: { id: number; seq: number; threadRootId?: number } = {
			id: message.id,
			seq: message.seq,
		}
		if (message.threadRootId !== undefined && message.threadRootId !== 0) {
			found.threadRootId = message.threadRootId
		}
		return found
	} catch {
		return null
	}
}

/**
 * A conversation the server ordered a resync for: the loaded threads are dropped before the window
 * is refetched, because what they were read against is gone and an open pane must ask again
 * (UX §4.6). The window is reopened by the same rule as an opening — the first unread when there
 * is one, else the tail — so a reconnect lands the reader where the opening did. This is what
 * chat's binding (`bind.ts`) registers as the resync handler.
 */
export function resyncConversation(conv: ConversationId): Promise<void> {
	client.api.setState(clearThreads)
	return openConversation(conv)
}

// ---- giving a window back ----

/**
 * The one timer of the module: the next moment at which a window may be given back. It is set when
 * a surface lets one go and never while one is on screen, so a tab standing in a conversation keeps
 * no timer at all, and a sweep that finds nothing to give back arms nothing either.
 */
let sweepTimer: ReturnType<typeof setTimeout> | undefined

function sweep(): void {
	sweepTimer = undefined
	const now = Date.now()
	// A window this tab has never had on screen — every one a warm boot restored — is given its own
	// half hour here rather than being taken for one whose surface is long gone (`trim.ts`).
	noteLoadedWindows(client.api.getState(), now)
	client.api.setState((model) => evictIdle(model, now))
	// What has been given back is no longer due, and saying so is what ends the sweep: a visit left
	// standing as due answers "now" for ever, and the arming below would then never stop. A window
	// the sweep refused — its page is in flight — is instead due again a whole idle period from now,
	// so the timer below comes back to it rather than it waiting for the next visit to end
	// (`trim.ts`). The model is read back because that is the one that says which is which.
	forgetSweptVisits(client.api.getState(), now)
	armSweep()
}

function armSweep(): void {
	if (sweepTimer !== undefined) return
	const due = nextEvictionDue(Date.now())
	if (due === undefined) return
	sweepTimer = setTimeout(sweep, due)
}

/**
 * What a surface calls while it shows a conversation's timeline: the window is held for as long as
 * the answer has not been called, and given back `EVICT_AFTER_MS` after it has (`trim.ts`). A reader
 * who opens forty conversations over a morning and reads in one of them holds one window, not forty.
 *
 * It is here rather than in `trim.ts` because giving a window back is a write of the model, and this
 * file is where the module writes what no event carries.
 */
export function visitConversation(conv: ConversationId): () => void {
	const release = holdConversation(conv)
	return () => {
		release()
		armSweep()
	}
}

/** The same for an open thread pane, whose replies are a window of their own. */
export function visitThread(rootId: number): () => void {
	const release = holdThread(rootId)
	return () => {
		release()
		armSweep()
	}
}

/** Stops the sweep and forgets it is due. For tests, which share a module with the tab they drive. */
export function stopSweeping(): void {
	if (sweepTimer !== undefined) clearTimeout(sweepTimer)
	sweepTimer = undefined
}

/**
 * A public channel this reader has never been in, put into the model by its address alone
 * (UX §8 #8). `/c/<slug>` resolves against the reader's own conversations, so without this step a
 * channel anybody may read answers "Nothing here answers to this address." and the *Join* banner
 * behind it is unreachable. Browsing is `chat.conversation.browse`, so the server answers only
 * what this account may see, and the row it hands back carries no membership: the surface then
 * draws the read-only history and the banner on its own.
 *
 * Answers whether the slug named a channel, so a caller can tell "not there" from "on its way".
 */
export async function loadPublicChannel(slug: string): Promise<boolean> {
	try {
		const { channels } = await browseChannels("", slug)
		const found = channels.find((row) => row.conversation.slug === slug)
		if (found === undefined) return false
		client.api.setState((model) => upsertBrowsedConversation(model, found.conversation))
		return true
	} catch {
		return false
	}
}

// ---- the administrator's outside view ----

/**
 * Writes one read of a conversation into the model by how the reader holds it. An administrator's
 * view of a conversation they are not in becomes an outside view and nothing else: its history may
 * not be read, so it must never look readable. A conversation the reader reaches by a group is
 * written as a browsed one, the way `loadPublicChannel` writes it; one they are in — they joined
 * meanwhile — with their row, so the composer is there before the socket repeats it. Either way an
 * outside view of it is over.
 */
function applyRead(model: Model, read: ConversationReadAnswer): Model {
	const { conversation, self } = read
	if (read.reach === ReachAdmin) return putOutside(model, conversation)
	const next = dropOutside(model, conversation.id)
	if (self?.state === "active") {
		return upsertSelf(upsertConversation(next, conversation), conversation.id, self)
	}
	return upsertBrowsedConversation(next, conversation)
}

/**
 * For an administrator: the conversation of the admin listing with this short name, read and kept
 * as an outside view (`ChatSlice.outside`). It is what a `/c/<slug>` address falls back to when
 * browsing found nothing — a private channel, or a channel no group of the administrator owns
 * (`routes.tsx`). The listing names only what the administrator is not in, never a direct
 * conversation, and has no query: the row is found here. Answers whether the slug named one.
 */
export async function loadOutsideConversation(slug: string): Promise<boolean> {
	try {
		const { conversations } = await listAdminConversations()
		const row = conversations.find((candidate) => candidate.slug === slug)
		if (row === undefined) return false
		const read = await readConversation(row.id)
		client.api.setState((model) => applyRead(model, read))
		return true
	} catch {
		return false
	}
}

/**
 * Reads one outside view again — or a channel the reader only browses through a group, which an
 * administrator may change without joining. Nothing the server publishes reaches a reader without a
 * row, so this is how the view learns what changed: after a write made from it
 * (`conversation/manage.ts`) and when an outside view's address is arrived at again (`routes.tsx`). A conversation the read no longer finds — deleted,
 * turned into something the administrator may not see, or the reader no longer an administrator — is
 * dropped, and its address then says that nothing answers to it. Any other failure keeps the view as
 * it last was; a re-read must never make the write before it look as if it had failed.
 */
export async function refreshOutside(conv: ConversationId): Promise<void> {
	try {
		const read = await readConversation(conv)
		client.api.setState((model) => applyRead(model, read))
	} catch (err) {
		if (err instanceof WireError && err.status === 404) {
			client.api.setState((model) => dropOutside(model, conv))
		}
	}
}
